import { supabase } from './supabase';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface ConversionConfig {
  send_to: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
}

interface PageTracking {
  page_slug: string;
  conversion_label: string;
  is_active: boolean;
}

let pageTrackingCache: Record<string, string> = {};
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000;

export const fetchPageTracking = async (): Promise<Record<string, string>> => {
  const now = Date.now();

  if (Object.keys(pageTrackingCache).length > 0 && (now - cacheTimestamp) < CACHE_DURATION) {
    return pageTrackingCache;
  }

  try {
    const { data, error } = await supabase
      .from('page_conversion_tracking')
      .select('page_slug, conversion_label, is_active')
      .eq('is_active', true);

    if (error) throw error;

    const tracking: Record<string, string> = {};
    data?.forEach((item: PageTracking) => {
      tracking[item.page_slug] = item.conversion_label;
    });

    pageTrackingCache = tracking;
    cacheTimestamp = now;

    return tracking;
  } catch (error) {
    console.error('Error fetching page tracking:', error);
    return pageTrackingCache;
  }
};

export const getPageConversionLabel = async (pageSlug: string): Promise<string | null> => {
  const tracking = await fetchPageTracking();
  return tracking[pageSlug] || null;
};

export const trackPageConversion = async (
  pageSlug: string,
  config?: Partial<ConversionConfig>
) => {
  if (typeof window.gtag === 'undefined') {
    console.warn('Google Ads tracking not loaded');
    return;
  }

  const conversionLabel = await getPageConversionLabel(pageSlug);

  if (!conversionLabel) {
    console.warn(`No active conversion label found for page: ${pageSlug}`);
    return;
  }

  const conversionConfig: ConversionConfig = {
    send_to: `AW-11399296120/${conversionLabel}`,
    ...config,
  };

  window.gtag('event', 'conversion', conversionConfig);
  console.log('Page conversion tracked:', pageSlug, conversionConfig);
};

export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window.gtag === 'undefined') {
    console.warn('Google Ads tracking not loaded');
    return;
  }

  window.gtag('event', eventName, eventParams);
  console.log('Event tracked:', eventName, eventParams);
};

export const trackButtonClick = (
  buttonId: string,
  eventData?: Record<string, any>
) => {
  trackEvent('button_click', {
    button_id: buttonId,
    ...eventData,
  });
};
